import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import { useState, ReactNode } from 'react';

export interface ConfirmationDialogProps {
	title: string;
	message: string | ReactNode;
  open: boolean;
  onCancel: () => void;
  onConfirm: (sendNotification?: boolean) => void;
  showNotificationCheckbox?: boolean;
  notificationCheckboxLabel?: string;
  notificationCheckboxTooltip?: string;
  loading?: boolean;
}

const ConfirmationDialog = ({
	title, 
	message, 
	open, 
	onCancel, 
	onConfirm, 
	showNotificationCheckbox = false,
	notificationCheckboxLabel = "Notify participants",
	notificationCheckboxTooltip = "Send notifications to participants",
	loading = false,
}: ConfirmationDialogProps) => {

	const [error, setError] = useState("");
	const [sendNotification, setSendNotification] = useState(true);
	const [localSubmitting, setLocalSubmitting] = useState(false);

  // Use the loading flag to handle cases where the confirmation must be handled by a datagrid's processRowUpdate
  // function (e.g. RouteInstancesDataGrid on the Manage Routes page)
	const submitting = loading || localSubmitting;

  const handleCancel = () => {
    if (submitting) return; // prevent closing while submitting
    onCancel();
		setError("");
		setSendNotification(true); // Reset to default
  };

  const handleConfirm = async () => {
		if (submitting) return;
		setLocalSubmitting(true);
		try
		{
			setError("");
    	await onConfirm(showNotificationCheckbox ? sendNotification : undefined);
		}
		catch (err: any)
		{
			setError(err.Error || err.message);
		}
    finally {
			setLocalSubmitting(false);
    }
  };

  return (
    <Dialog
      sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
      maxWidth="xs"
      open={open}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {message}
        {showNotificationCheckbox ? (
          <Tooltip title={notificationCheckboxTooltip} placement="top">
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  color="primary"
                />
              }
              label={notificationCheckboxLabel}
              sx={{ mt: 2 }}
            />
          </Tooltip>
        ): null}
			{error != "" &&
				<Alert style={{marginTop: "10px"}} severity="error">{error}</Alert>
			}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmationDialog;